import { useLayoutEffect, useRef } from "react";
import { BrandMark, Skeleton } from "@cntrl/ui";
import type { LiveActivityContentState } from "../dayOfShow.js";
import styles from "./DosLiveCard.module.scss";

/**
 * The DOS Live Activity, drawn in the app (PO, ART-284): the show-day home card is a REPLICA of the lock-screen
 * widget (`DosLiveActivityWidget.swift`, `DosLockScreenView`) — identity row with the SOON / LIVE chip, the pre-show
 * countdown, the phase over its time window beside UP NEXT, the progress bar (or DOORS OPEN), and the venue.
 *
 * It renders a {@link LiveActivityContentState} and nothing else, so it can only ever say what the widget says: the
 * state comes from the same `buildLiveActivityPlan` the phone registers, picked at `nowMs` by `liveActivityStateAt`.
 * The two native timers (the countdown and the progress bar) are computed here from `nowMs`, which the caller ticks.
 */
export function DosLiveCard({
  state,
  bandLabel,
  nowMs,
  onOpen,
}: {
  state: LiveActivityContentState;
  bandLabel: string;
  nowMs: number;
  onOpen?: () => void;
}) {
  const pre = state.isPreShow;
  const countdownMs = pre && state.countdownTo ? state.countdownTo * 1000 - nowMs : null;
  const progress =
    !pre && !state.isInstant && state.phaseStart != null && state.phaseEnd != null && state.phaseEnd > state.phaseStart
      ? Math.min(1, Math.max(0, (nowMs / 1000 - state.phaseStart) / (state.phaseEnd - state.phaseStart)))
      : null;

  return (
    <section className={styles.card} aria-label="Day of show, live">
      <button type="button" className={styles.hit} onClick={onOpen} aria-label="Open Day of Show" disabled={!onOpen} />
      <div className={styles.idRow}>
        <span className={styles.band}>
          <BrandMark size={17} decorative className={styles.mark} />
          <span className={styles.bandName}>{bandLabel.toUpperCase()}</span>
        </span>
        <span className={styles.chip}>
          <span className={styles.dot} data-tone={pre ? "soon" : "live"} aria-hidden="true" />
          {pre ? "SOON" : "LIVE"}
        </span>
      </div>

      {pre && (
        <div className={styles.eyebrow}>
          {countdownMs != null && countdownMs > 0 ? `NEXT IN ${timer(countdownMs)}` : state.eyebrow}
        </div>
      )}

      <div className={styles.phaseRow}>
        <div className={styles.phaseCol}>
          <FitText className={styles.phase} text={state.phaseLabel} />
          <span className={styles.window}>{state.timeWindow}</span>
        </div>
        {state.nextValue && (
          <div className={styles.nextCol}>
            <span className={styles.nextLabel}>{state.nextLabel}</span>
            <span className={styles.nextValue}>{state.nextValue}</span>
          </div>
        )}
      </div>

      {state.isInstant ? (
        <span className={styles.doors}>
          <span className={styles.dot} data-tone="white" aria-hidden="true" />
          DOORS OPEN
        </span>
      ) : progress != null ? (
        <div
          className={styles.bar}
          role="progressbar"
          aria-label={`${state.phaseLabel} progress`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress * 100)}
        >
          <span className={styles.fill} style={{ inlineSize: `${progress * 100}%` }} />
        </div>
      ) : null}

      {state.venue && <div className={styles.venue}>{state.venue.toUpperCase()}</div>}
    </section>
  );
}

/** The widget's 28pt phase line shrinks a long act name to fit beside UP NEXT — never truncated — down to 60% of its
 *  size (SwiftUI's `minimumScaleFactor(0.6)`); only past that does it ellipsize. Re-fits when the card resizes. */
const PHASE_SIZE = 28;
const MIN_SCALE = 0.6;

function FitText({ text, className }: { text: string; className: string }) {
  const ref = useRef<HTMLHeadingElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fit = () => {
      el.style.fontSize = `${PHASE_SIZE}px`;
      const { scrollWidth, clientWidth } = el;
      if (scrollWidth > clientWidth && clientWidth > 0) {
        el.style.fontSize = `${Math.max(PHASE_SIZE * MIN_SCALE, (PHASE_SIZE * clientWidth) / scrollWidth)}px`;
      }
    };
    fit();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text]);
  return <h2 ref={ref} className={className}>{text}</h2>;
}

/**
 * The live card while its Day of Show data loads — the SAME dark card, with bones where each line will land, so a
 * show day's home never flashes the white call-times card on its way to the dark one. Sizes track the real lines:
 * the 28px phase, the 12px window, the up-next column, the 4px bar and the venue.
 */
export function DosLiveCardSkeleton() {
  return (
    <section className={styles.card} aria-label="Day of show, loading" aria-busy="true" data-testid="dos-live-skeleton">
      <div className={styles.idRow}>
        <span className={styles.band}>
          <BrandMark size={17} decorative className={styles.mark} />
          <Skeleton className={styles.bone} width={96} height={10} />
        </span>
        <Skeleton className={styles.bone} width={58} height={21} />
      </div>
      <div className={styles.phaseRow}>
        <div className={styles.phaseCol}>
          <Skeleton className={styles.bone} width="58%" height={30} />
          <Skeleton className={styles.bone} width={128} height={12} />
        </div>
        <div className={styles.nextCol}>
          <Skeleton className={styles.bone} width={48} height={8} />
          <Skeleton className={styles.bone} width={112} height={11} />
        </div>
      </div>
      <div className={styles.bar} />
      <Skeleton className={`${styles.bone} ${styles.venueBone}`} width={160} height={10} />
    </section>
  );
}

/** "1:23:45" / "23:45" — SwiftUI's `Text(timerInterval:showsHours:)` format, so the card reads like the widget. */
function timer(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
}
